import * as React from "react";
import { Eye, EyeOff } from "lucide-react";

import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import { cn } from "@/lib/utils";
import {
  getBrokerCredentialInputAttributes,
  isBinanceApiSecretField,
  isBrokerAccessTokenCredentialField,
} from "@/utils/binanceBrokerCredentials";

type BrokerMaskedCredentialInputProps = {
  id: string;
  field: string;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
};

export function BrokerMaskedCredentialInput({
  id,
  field,
  value,
  placeholder,
  onChange,
}: BrokerMaskedCredentialInputProps): React.ReactElement {
  const [revealed, setRevealed] = React.useState(false);
  const label = isBrokerAccessTokenCredentialField(field)
    ? "Access token"
    : isBinanceApiSecretField(field) || field.toLowerCase().includes("secret")
      ? "API secret"
      : "API key";

  return (
    <InputGroup
      data-broker-masked-credential-input=""
      className="h-8 w-full rounded-[6px] border-[#d3d3d3] font-[family-name:var(--font-primary)]"
    >
      <InputGroupInput
        id={id}
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        data-broker-masked-revealed={revealed ? "true" : undefined}
        className={cn(
          "text-sm normal-case tracking-normal",
          !revealed && "[-webkit-text-security:disc] [text-security:disc]"
        )}
        {...getBrokerCredentialInputAttributes(field)}
      />
      <InputGroupAddon align="inline-end" className="pr-1">
        <InputGroupButton
          type="button"
          size="icon-xs"
          variant="ghost"
          aria-label={revealed ? `Hide ${label}` : `Show ${label}`}
          aria-pressed={revealed}
          onClick={() => setRevealed((prev) => !prev)}
          className="text-gray-500 hover:text-gray-500"
        >
          {revealed ? <Eye aria-hidden /> : <EyeOff aria-hidden />}
        </InputGroupButton>
      </InputGroupAddon>
    </InputGroup>
  );
}
